import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, UserPlus, X } from 'lucide-react';
import UserAvatar from '../ui/UserAvatar';
import { isCompleteEmail } from '../chatHelpers';
import chatService from '../../services/chatService';

/**
 * "Add people" for a new channel: exact-email lookup (debounced 250 ms, the
 * same rule as ChatTitleBarSearch — there is no user directory), picked people
 * as removable chips, results in a dropdown under the field.
 */
export default function ChannelMemberPicker({ selected, onChange }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const emailMode = isCompleteEmail(query);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!emailMode) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setResults([]);
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      chatService
        .searchUsers(query.trim())
        .then((users) => {
          if (cancelled) return;
          setResults(Array.isArray(users) ? users : []);
          setSearching(false);
        })
        .catch(() => {
          if (cancelled) return;
          setResults([]);
          setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
  }, [query, emailMode]);

  const isPicked = (u) => selected.some((s) => s.id === u.id);
  const pick = (u) => {
    if (isPicked(u)) return;
    onChange([...selected, u]);
    setQuery('');
    setResults([]);
  };

  return (
    <div className="ncm-people">
      {selected.length > 0 && (
        <ul className="ncm-chips" aria-label={t('chat.newChannelModal.people')}>
          {selected.map((u) => (
            <li key={u.id} className="ncm-chip">
              <UserAvatar userId={u.id} name={u.name} size={18} />
              <span className="ncm-chip-name">{u.name}</span>
              <button
                type="button"
                className="ncm-chip-remove"
                aria-label={t('chat.newChannelModal.removePerson', { name: u.name })}
                onClick={() => onChange(selected.filter((s) => s.id !== u.id))}
              >
                <X size={12} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="ncm-field ncm-field--icon">
        <Search size={14} className="ncm-field-icon" aria-hidden />
        <input
          type="text"
          className="modal-invite-input modal-input-flat"
          placeholder={t('chat.newChannelModal.peoplePlaceholder')}
          aria-label={t('chat.newChannelModal.people')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const first = results.find((u) => !isPicked(u));
            if (first) pick(first);
          }}
        />
      </div>

      {emailMode ? (
        <div className="ncm-results" role="listbox" aria-label={t('chat.newChannelModal.people')}>
          {results.map((u) => {
            const already = isPicked(u);
            return (
              <button
                key={u.id}
                type="button"
                role="option"
                aria-selected={false}
                disabled={already}
                className="ncm-result"
                onClick={() => pick(u)}
              >
                <UserAvatar userId={u.id} name={u.name} size={26} />
                <span className="ncm-result-text">
                  <span className="ncm-result-name">{u.name}</span>
                  <span className="ncm-result-sub">{already ? t('chat.newChannelModal.alreadyAdded') : u.email}</span>
                </span>
                {!already && <UserPlus size={14} className="ncm-result-add" aria-hidden />}
              </button>
            );
          })}
          {results.length === 0 && (
            <div className="ncm-results-empty" role="status">
              {searching ? t('chat.newChannelModal.searching') : t('chat.newChannelModal.notFound')}
            </div>
          )}
        </div>
      ) : (
        <p className="ncm-hint">{t('chat.newChannelModal.peopleHint')}</p>
      )}
    </div>
  );
}
